import {largViewportSize, FxxlSetupSlider2} from '@fxxl/globals';
import FxxlError from '@fxxl/error';

export function setup()
{
    for (const triggerElement of document.querySelectorAll('.fxxl-json-modal')) {
        new JsonModal(triggerElement);
    }
}

/**
 * Diese Klasse erlaubt es, beim Klick auf ein Element JSON-Daten zu laden, und diese in einem Modal anzuzeigen.
 *
 * - Das klickbare Element MUSS das Attribut `data-json-url` enthalten, welches die URL für die JSON-Datei enthält.
 *   Aus Sicherheitsgründen sind nur lokale URLs erlaubt.
 * - Das klickbare Element DARF das Attribut `data-modal-heading` enthalten, welches den Titel des Modals enthält.
 *   Alternativ kann die JSON-Antwort ein `modal_heading`-Attribut enthalten. Ist beides spezifiziert, wird der Wert
 *   aus der JSON-Antwort verwendet.
 * - Die JSON-Antwort MUSS folgende Form haben:
 *     * `modal_content`, required: der Inhalt des Modals als HTML-String.
 *     * `modal_heading`, optional: der Titel des Modals.
 *     * `modal_content_class`, optional: eine Klasse, die dem div, dass den Inhalt des Modals enthält, hinzugefügt wird.
 */
export class JsonModal
{
    static loadingClass = 'fxxl-json-modal-loading';

    constructor(triggerElement)
    {
        this.triggerElement = triggerElement;
        this.modalHeading = '';
        this.modal = null;
        this.readConfigFromDom();
        this.attachEventHandlers();
    }

    /**
     * Liest die Konfiguration aus den data-Attributen des klickbaren Elements.
     */
    readConfigFromDom()
    {
        this.jsonUrl = this.triggerElement.dataset.jsonUrl;
        this.modalHeading = this.triggerElement.dataset.modalHeading ?? '';
    }

    /**
     * Registriert die Event-Handler für das klickbare Element.
     */
    attachEventHandlers()
    {
        this.triggerElement.addEventListener('click', this.handleClick.bind(this));
    }

    /**
     * Behandelt das Klick-Event.
     *
     * - Falls das Modal bereits existiert, wird es geöffnet.
     *   Sonst:
     * - Laden er JSON-Daten per Fetch-API.
     * - Bei Erfolg, erzeugen des Modals und öffnen.
     */
    async handleClick(event)
    {
        event.preventDefault();
        event.stopPropagation();
        if (!this.jsonUrl) {
            new FxxlError('Missing URL for JSON data.');
            return;
        }
        if (this.triggerElement.classList.contains(JsonModal.loadingClass)) {
            return;
        }
        this.triggerElement.classList.add(JsonModal.loadingClass);
        if (this.modal === null) {
            const response = await this.fetchJson();
            await this.createModal(response);
        }
        this.modal.open();
        this.triggerElement.classList.remove(JsonModal.loadingClass);
    }

    /**
     * Erzeugt das Modal initialisiert es.
     */
    async createModal(response)
    {
        if (typeof response['modal_content'] !== 'undefined') {
            const modalContent = response['modal_content'];
            let modalHeading = this.modalHeading;
            if (typeof response['modal_heading'] !== 'undefined') {
                modalHeading = response['modal_heading'];
            }
            let modalContentClass = '';
            if (typeof response['modal_content_class'] === 'string') {
                modalContentClass = response['modal_content_class'];
            }
            this.initModal(modalContent, modalHeading, modalContentClass);
        }
    }

    /**
     * Lädt die JSON-Daten.
     */
    async fetchJson()
    {
        const response = await fetch(this.jsonUrl);
        return response.json();
    }

    /**
     * Initialisiert das Modal.
     */
    initModal(content, headerText = '', contentClass = '')
    {
        const modalElement = this.createModalElement(content, headerText, contentClass);
        this.modal = M.Modal.init(modalElement);
        modalElement.querySelectorAll('.js-fxxl-product-cards-slider').forEach((sliderElement) => {
            if (isLargeViewport() === true) {
                sliderElement.setAttribute("data-remove-margin", "true");
            }
            var sliderId = sliderElement.getAttribute('id');
            new window.FxxlSetupSlider2(sliderId, window.largViewportSize);
        });

        document.querySelectorAll('.fxxl-slider-loader').forEach((element) => {
            element.classList.add('fxxl-slider-loader--loaded');
        });
    }

    /**
     * Erzeugt die DOM-Elemente für das Modal und fügt sie dem Dokument hinzu.
     */
    createModalElement(content, headerText = '', contentClass = '')
    {
        const element = document.createElement('div');
        element.classList.add('fxxl-modal', 'modal', 'fxxl-modal--mobile-fullscreen', 'fxxl-modal--json');
        element.id = 'fxxl-json-modal-' + window.M.guid();
        element.innerHTML = `
            <div class="fxxl-modal__container--json">
                <div class="fxxl-modal__header modal-header">
                    <div class="fxxl-modal__header-text">${headerText}</div>
                        <a class=" fxxl-modal__header-close modal-close">
                            <svg width="24" height="24"><use href="#sprite_icon_close" xlink:href="#sprite_icon_close"></use></svg>
                        </a>
                    </div>
                <div class="fxxl-modal__content modal-content ${contentClass}" >${content}</div>
            </div>
        `;
        document.body.appendChild(element);
        return element;
    }
}